// The one set of selected notes on the current board. Keyboard and group-drag moves act on it, and it is the
// selection the browser-AI view reports for organisers, so "selected" means the same thing everywhere.
// Selection is local to this browser and never broadcast to other users.
import { ComputedRef, computed, reactive } from 'vue'

interface State {
  boardId: string
  selectedNoteIds: string[]
}

const state: State = reactive({
  boardId: '',
  selectedNoteIds: [],
})

interface Actions {
  setBoard: (boardId: string) => void
  toggleNote: (noteId: string) => void
  selectOnly: (noteId: string) => void
  clearSelection: () => void
}

interface Getters {
  selectedNoteIds: ComputedRef<string[]>
  selectedCount: ComputedRef<number>
  isSelected: (noteId: string) => boolean
}

function useNoteSelection(): { state: State; actions: Actions; getters: Getters } {
  const actions: Actions = {
    // Positions are per board, so a selection never carries over to another board
    setBoard(boardId: string): void {
      if (boardId === state.boardId) return
      state.boardId = boardId
      state.selectedNoteIds = []
    },

    toggleNote(noteId: string): void {
      const index = state.selectedNoteIds.indexOf(noteId)
      if (index === -1) state.selectedNoteIds.push(noteId)
      else state.selectedNoteIds.splice(index, 1)
    },

    selectOnly(noteId: string): void {
      state.selectedNoteIds = [noteId]
    },

    clearSelection(): void {
      state.selectedNoteIds = []
    },
  }

  const getters: Getters = {
    selectedNoteIds: computed(() => state.selectedNoteIds),
    selectedCount: computed(() => state.selectedNoteIds.length),
    isSelected: (noteId: string) => state.selectedNoteIds.includes(noteId),
  }

  return { state, actions, getters }
}

export default useNoteSelection
